import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Database, Download, Upload, CheckCircle2, AlertCircle, KeyRound, Sparkles, ShieldAlert } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isLeader = user?.role === "team_leader";
  const [dbStatus, setDbStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const fileRef = useRef(null);
  const [wipe, setWipe] = useState(false);
  const [pw, setPw] = useState({ old: "", n: "" });
  const [seeding, setSeeding] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [keepUsers, setKeepUsers] = useState(true);
  const [resetting, setResetting] = useState(false);

  const testDb = async () => {
    setTesting(true);
    setDbStatus(null);
    try {
      const { data } = await api.get("/settings/db-test");
      setDbStatus(data);
    } catch (err) {
      setDbStatus({ ok: false, error: formatApiError(err?.response?.data?.detail) });
    } finally { setTesting(false); }
  };

  const backup = async () => {
    try {
      const { data } = await api.get("/settings/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `presales-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup berhasil diunduh");
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const doRestore = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const payload = { data: parsed.data || parsed, wipe };
      const { data } = await api.post("/settings/restore", payload);
      toast.success(`Restore selesai: ${Object.values(data.restored).reduce((a, b) => a + b, 0)} dokumen`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "File tidak valid"); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pw.n });
      toast.success("Password diperbarui");
      setPw({ old: "", n: "" });
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const seedDummy = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/settings/seed-dummy");
      const total = Object.values(data.inserted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Data dummy berhasil ditambahkan (${total} dokumen)`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSeeding(false); }
  };

  const doReset = async () => {
    if (resetConfirm !== "RESET") { toast.error("Ketik RESET untuk konfirmasi"); return; }
    setResetting(true);
    try {
      const { data } = await api.post("/settings/reset-db", { keep_users: keepUsers, confirm: "RESET" });
      const total = Object.values(data.wiped || {}).reduce((a, b) => a + b, 0);
      toast.success(`Database di-reset (${total} dokumen dihapus)`);
      setResetOpen(false);
      setResetConfirm("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setResetting(false); }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="label-mini">Setting</div>
        <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Konfigurasi</h1>
        <p className="text-sm text-muted-foreground mt-2">DB test, backup/restore, dan preferensi akun.</p>
      </div>

      {/* Account password */}
      <div className="card-tech p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-4 w-4" />
          <h3 className="font-heading text-xl font-bold">Ganti Password</h3>
        </div>
        <form onSubmit={changePw} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label className="label-mini">Password Baru</Label>
            <Input required minLength={6} type="password" value={pw.n} onChange={(e) => setPw({ ...pw, n: e.target.value })} data-testid="settings-newpw-input" /></div>
          <div className="md:col-span-2 flex items-end">
            <Button type="submit" className="rounded-sm" data-testid="settings-change-pw-button">Simpan</Button>
          </div>
        </form>
      </div>

      {isLeader ? (
        <>
          {/* DB Config & Test */}
          <div className="card-tech p-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                <h3 className="font-heading text-xl font-bold">Konfigurasi & Test Database</h3>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={testDb} disabled={testing} className="rounded-sm" data-testid="test-db-button">
                  {testing ? "Menguji..." : "Test Koneksi DB"}
                </Button>
                <Button
                  onClick={seedDummy}
                  disabled={seeding}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="seed-dummy-button"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {seeding ? "Menambahkan..." : "Tambah Data Dummy"}
                </Button>
                <Button
                  onClick={() => setResetOpen(true)}
                  variant="destructive"
                  className="rounded-sm"
                  data-testid="reset-db-button"
                >
                  <ShieldAlert className="h-4 w-4 mr-2" />
                  Reset Database
                </Button>
              </div>
            </div>
            {dbStatus && (
              <div className={`rounded-sm border p-4 ${dbStatus.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="db-status-panel">
                <div className="flex items-center gap-2 mb-3">
                  {dbStatus.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <AlertCircle className="h-4 w-4 text-red-700" />}
                  <div className="font-semibold text-sm">{dbStatus.ok ? "Koneksi berhasil" : "Koneksi gagal"}</div>
                </div>

                {/* Connection config always shown so user knows what was tested */}
                <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mono ${dbStatus.ok ? "text-emerald-900" : "text-red-900"}`} data-testid="db-config-panel">
                  <div><div className="label-mini">Host</div>{dbStatus.host || "-"}</div>
                  <div><div className="label-mini">Port</div>{dbStatus.port || "-"}</div>
                  <div><div className="label-mini">Username</div>{dbStatus.username || "(none)"}</div>
                  <div><div className="label-mini">Password</div>{dbStatus.password_masked || "(none)"}</div>
                  <div className="col-span-2"><div className="label-mini">Database</div>{dbStatus.db_name}</div>
                  <div className="col-span-2"><div className="label-mini">Scheme</div>{dbStatus.uri_scheme || "mongodb"}</div>
                </div>

                {dbStatus.ok ? (
                  <div className="text-xs mono grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-emerald-200 text-emerald-900">
                    <div><div className="label-mini">MongoDB</div>v{dbStatus.version}</div>
                    <div><div className="label-mini">Collections</div>{dbStatus.collections?.length ?? 0}</div>
                    <div><div className="label-mini">Data Size</div>{Math.round((dbStatus.data_size || 0) / 1024)} KB</div>
                    <div><div className="label-mini">Storage</div>{Math.round((dbStatus.storage_size || 0) / 1024)} KB</div>
                  </div>
                ) : (
                  <div className="text-xs mono text-red-900 mt-3 pt-3 border-t border-red-200">{dbStatus.error}</div>
                )}
              </div>
            )}
          </div>

          {/* Backup & Restore */}
          <div className="card-tech p-6">
            <h3 className="font-heading text-xl font-bold mb-4">Backup & Restore</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-border rounded-sm p-5">
                <div className="flex items-center gap-2 mb-2"><Download className="h-4 w-4" /><div className="font-semibold">Backup (Download JSON)</div></div>
                <p className="text-xs text-muted-foreground mb-4">Semua data (users, projects, timesheet, performance, ...) di-export sebagai file JSON tunggal.</p>
                <Button onClick={backup} className="rounded-sm w-full" data-testid="backup-button"><Download className="h-4 w-4 mr-2" /> Download Backup</Button>
              </div>
              <div className="border border-border rounded-sm p-5">
                <div className="flex items-center gap-2 mb-2"><Upload className="h-4 w-4" /><div className="font-semibold">Restore (Upload JSON)</div></div>
                <p className="text-xs text-muted-foreground mb-3">Upload file backup untuk memulihkan data. Aktifkan Wipe untuk mengganti semua data.</p>
                <label className="flex items-center gap-2 text-xs mb-3">
                  <input type="checkbox" checked={wipe} onChange={(e) => setWipe(e.target.checked)} data-testid="wipe-toggle" />
                  Wipe collections sebelum restore
                </label>
                <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={(e) => doRestore(e.target.files?.[0])} data-testid="restore-file-input" />
                <Button variant="outline" className="rounded-sm w-full" onClick={() => fileRef.current?.click()} data-testid="restore-button">
                  <Upload className="h-4 w-4 mr-2" /> Pilih file JSON
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="card-tech p-6 text-sm text-muted-foreground">
          Fitur Test DB dan Backup/Restore hanya tersedia untuk Team Leader.
        </div>
      )}

      {/* Reset DB confirm dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" /> Reset Database
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Aksi ini akan menghapus <span className="font-semibold text-foreground">semua data</span>
              {" "}(projects, clients, PM, KPI, timesheet, template). Aksi tidak dapat dibatalkan.
            </p>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={keepUsers}
                onChange={(e) => setKeepUsers(e.target.checked)}
                className="mt-1"
                data-testid="reset-keep-users"
              />
              <div>
                <div className="font-semibold">Simpan akun user</div>
                <div className="text-xs text-muted-foreground">
                  Jika di-uncheck, semua user termasuk akun ini akan dihapus. Akun awal akan otomatis di-seed ulang.
                </div>
              </div>
            </label>
            <div>
              <Label className="label-mini">Ketik "RESET" untuk konfirmasi</Label>
              <Input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="RESET"
                data-testid="reset-confirm-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="reset-cancel-button">Batal</Button>
              <Button
                variant="destructive"
                disabled={resetting || resetConfirm !== "RESET"}
                onClick={doReset}
                data-testid="reset-confirm-button"
              >
                {resetting ? "Mereset..." : "Reset Sekarang"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
